import React from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import SearchableSelect from '@/components/shared/SearchableSelect';
import {
  bsiInternetBankApi,
  type BsiIbAccountOption,
  type BsiIbDestinationAccount,
} from '@/lib/banks/bsi/api';
import { isSessionExpiredMessage, type BsiFlashMessage } from './helpers';

type Props = {
  sessionValid: boolean;
  onMessage: (message: BsiFlashMessage | null) => void;
  onSessionExpired: () => void;
};

const BsiDestinationsTab: React.FC<Props> = ({ sessionValid, onMessage, onSessionExpired }) => {
  const [transferTypes, setTransferTypes] = React.useState<BsiIbAccountOption[]>([]);
  const [transferType, setTransferType] = React.useState('LOCAL');
  const [items, setItems] = React.useState<BsiIbDestinationAccount[]>([]);
  const [loadingForm, setLoadingForm] = React.useState(false);
  const [loadingList, setLoadingList] = React.useState(false);

  const loadForm = React.useCallback(async () => {
    setLoadingForm(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.destinationForm();
      setTransferTypes(res.data.transfer_types || []);
      setTransferType((prev) => prev || res.data.transfer_types?.[0]?.value || 'LOCAL');
    } catch (e) {
      onMessage({ type: 'error', text: e instanceof Error ? e.message : 'خطا در فرم حساب مقصد' });
    } finally {
      setLoadingForm(false);
    }
  }, [onMessage]);

  React.useEffect(() => {
    if (sessionValid && transferTypes.length === 0 && !loadingForm) void loadForm();
  }, [sessionValid, transferTypes.length, loadingForm, loadForm]);

  const handleFetch = async () => {
    setLoadingList(true);
    onMessage(null);
    try {
      const res = await bsiInternetBankApi.destinationAccounts({
        transfer_type: transferType || 'LOCAL',
      });
      setItems(res.data.items || []);
      onMessage({ type: 'success', text: res.message || 'حساب‌های مقصد دریافت شد.' });
    } catch (err) {
      const text = err instanceof Error ? err.message : 'دریافت حساب مقصد ناموفق بود.';
      onMessage({ type: 'error', text });
      if (isSessionExpiredMessage(text)) onSessionExpired();
    } finally {
      setLoadingList(false);
    }
  };

  return (
    <>
      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="text-base">حساب‌های مقصد</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!sessionValid ? (
            <p className="text-sm text-amber-700">برای مشاهده حساب‌های مقصد، ابتدا وارد اینترنت‌بانک شوید.</p>
          ) : (
            <>
              <p className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                فقط خواندن لیست حساب‌های مقصد ذخیره‌شده در بانک (بدون انتقال وجه).
              </p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="min-w-[220px] space-y-2">
                  <Label>نوع انتقال</Label>
                  <SearchableSelect
                    value={transferType}
                    onChange={setTransferType}
                    options={
                      transferTypes.length
                        ? transferTypes.map((t) => ({ value: t.value, label: t.label || t.value }))
                        : [
                            { value: 'LOCAL', label: 'داخل بانکی' },
                            { value: 'INTRABANK', label: 'بین بانکی' },
                            { value: 'C2C', label: 'کارت به کارت' },
                          ]
                    }
                    placeholder={loadingForm ? 'در حال دریافت…' : 'نوع'}
                    disabled={loadingForm}
                  />
                </div>
                <Button type="button" variant="outline" onClick={() => void loadForm()} disabled={loadingForm}>
                  {loadingForm ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  <span className="mr-1">بروزرسانی انواع</span>
                </Button>
                <Button type="button" onClick={() => void handleFetch()} disabled={loadingList}>
                  {loadingList ? <Loader2 className="ml-1 h-4 w-4 animate-spin" /> : null}
                  بارگذاری لیست
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {items.length > 0 && (
        <div className="lg:col-span-3">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">حساب‌های مقصد — {items.length} مورد</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded border">
                <table className="w-full min-w-[700px] text-sm">
                  <thead>
                    <tr className="bg-slate-100 text-right">
                      <th className="border px-2 py-2">#</th>
                      <th className="border px-2 py-2">نام صاحب حساب</th>
                      <th className="border px-2 py-2">شماره حساب / کارت</th>
                      <th className="border px-2 py-2">برچسب</th>
                      <th className="border px-2 py-2">کد شعبه</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((d) => (
                      <tr key={`${d.row}-${d.account_number}`}>
                        <td className="border px-2 py-1.5">{d.row}</td>
                        <td className="border px-2 py-1.5">{d.owner_name || '—'}</td>
                        <td className="border px-2 py-1.5 font-mono text-xs">{d.account_number || '—'}</td>
                        <td className="border px-2 py-1.5">{d.account_label || '—'}</td>
                        <td className="border px-2 py-1.5">{d.branch_code || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </>
  );
};

export default BsiDestinationsTab;
